import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
// Response schemas for clusters (the flat sibling of zones). Field-exposure rule
// (ANATOMY §L7): the list row carries what the list + its coverage cell render,
// plus the parent zone (the flat-with-zone-column archetype); the detail adds the
// (deferred) contents counts. No `display_order` — clusters aren't reorderable.
// ── GET /api/projects/:projectId/clusters — list row ────────────────────────
// `zone_id` + `zone_name` surface the parent (the list's zone column + filter).
// `assigned_people_count` (distinct people across the cluster's cluster-level
// chains) + `required_unfilled_count` (required cluster-level responsibilities with
// no lead) feed the shipped `ResponsibilityCoverage` cell. Both 0 when the
// field-force module is off — the column is hidden then.
export const ClusterListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    zone_id: z.number().int(),
    zone_name: z.string().max(32),
    is_default: z.boolean(),
    is_disabled: z.boolean(),
    assigned_people_count: z.number().int(),
    required_unfilled_count: z.number().int(),
    // ADR-052 §5 proactive surfacing: may THIS viewer manage this cluster's responsibility
    // chains? (users:manage + level-set ∋ cluster + own-scope reaches it). The list greys
    // the manage affordance where false (a dual-audience viewer sees clusters outside their
    // own scope but can't assign there) — surfaced, not failed-on-click.
    can_manage_responsibilities: z.boolean(),
    // May THIS viewer place a site into it — the viewer's own CLUSTER reach, the same
    // resolver the create write enforces (ADR-056 §4: a see-all viewer every cluster; a
    // direct site grant reaches no cluster). The site form greys the rest ("Outside
    // your scope"); the server still refuses on save.
    placeable: z.boolean(),
})
    .strict();
export const ClustersListResponseSchema = z
    .object({ items: z.array(ClusterListItemSchema) })
    .strict();
// ── GET /api/projects/:projectId/clusters/:clusterId — detail ───────────────
// The contents counts feed the read-only `StatStrip` — the deferred count
// drill-through seam: 0 until sites / warehouses / vehicle-pools land (the value
// slot already takes a node so a future link drops in without a wire change).
export const ClusterDetailSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    zone_id: z.number().int(),
    zone_name: z.string().max(32),
    is_default: z.boolean(),
    is_disabled: z.boolean(),
    site_count: z.number().int(),
    warehouse_count: z.number().int(),
    vehicle_pool_count: z.number().int(),
    // ADR-052 §5 proactive surfacing — same per-cluster own-scope flag the list row carries,
    // so the peek's manage affordance greys (Tooltip) for a capable viewer outside their
    // own scope rather than failing on click. Computed by the SAME checker as the list.
    can_manage_responsibilities: z.boolean(),
})
    .strict();
// ── POST create — minimal id (FE refetches the list) ────────────────────────
export const CreatedClusterResponseSchema = z.object({ id: z.number().int() }).strict();
